import React, { useEffect, useState, createContext, useContext } from 'react';
import { signIn, signUp, signOut, getCurrentUser, getSession } from '../services/supabaseAuthService';
import { createProfile } from '../services/profileService';
interface User {
  id: string;
  email: string;
  type: 'brand' | 'organizer' | 'admin';
  name: string;
}
interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  loginAsDemo: (role: 'brand' | 'organizer' | 'super_admin') => Promise<void>;
  register: (email: string, password: string, role: 'brand' | 'organizer') => Promise<void>;
  logout: () => void;
  loading: boolean;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
export function AuthProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    // Check for existing session on load
    const checkSession = async () => {
      try {
        const session = await getSession();
        if (session) {
          const user = await getCurrentUser();
          if (user && user.user_metadata) {
            setUser({
              id: user.id,
              email: user.email || '',
              type: user.user_metadata.type || 'brand',
              name: user.user_metadata.name || ''
            });
          }
        }
      } catch (error) {
        console.error('Session check error:', error);
      } finally {
        setLoading(false);
      }
    };
    checkSession();
  }, []);
  const login = async (email: string, password: string) => {
    try {
      const {
        user
      } = await signIn(email, password);
      if (user && user.user_metadata) {
        const loggedInUser = {
          id: user.id,
          email: user.email || '',
          type: user.user_metadata.type || 'brand',
          name: user.user_metadata.name || ''
        };
        setUser(loggedInUser);
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };
  const loginAsDemo = async (role: 'brand' | 'organizer' | 'super_admin') => {
    try {
      const user = await authService.loginAsDemo(role);
      setUser(user);
    } catch (error) {
      console.error('Demo login failed:', error);
      throw error;
    }
  };
  const register = async (email: string, password: string, role: 'brand' | 'organizer') => {
    try {
      // Sign up the user with metadata
      const {
        user
      } = await signUp(email, password, {
        type: role,
        name: ''
      });
      if (!user) {
        throw new Error('Registration failed');
      }
      // Create a profile for the new user
      await createProfile({
        role: role === 'brand' ? 'Brand' : 'Organizer',
        name: '',
        description: '',
        whatTheySeek: {
          sponsorshipTypes: [],
          budgetRange: ''
        }
      });
      const newUser = {
        id: user.id,
        email: user.email || '',
        type: role,
        name: ''
      };
      setUser(newUser);
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  };
  const logout = async () => {
    try {
      await signOut();
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };
  return <AuthContext.Provider value={{
    user,
    login,
    loginAsDemo,
    register,
    logout,
    loading
  }} data-id="element-1134">
      {children}
    </AuthContext.Provider>;
}
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}